'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Check, 
  X, 
  ArrowRight, 
  AlertCircle, 
  Download, 
  Server,
  Phone,
  Mail,
  MessageSquare
} from 'lucide-react';
import { collectUserTelemetry } from '../../lib/telemetry';
import { useForensicsTracker } from '../../lib/useForensicsTracker';
import { useSessionStore } from '../../store/useSessionStore';

export interface VaultHandshakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultStep?: number;
}

export const VaultHandshakeModal: React.FC<VaultHandshakeModalProps> = ({
  isOpen,
  onClose,
  defaultStep = 1
}) => {
  const { getTelemetrySnapshot } = useForensicsTracker();
  const { 
    sessionToken, 
    nodeId,
    evaluationData,
    calculatedValuationMln
  } = useSessionStore();

  const [step, setStep] = useState<number>(defaultStep);

  // Form State (Zero-Party Non-PII Intake)
  const [revenueBand, setRevenueBand] = useState<string>('100–250 млн ₽');
  const [payrollModel, setPayrollModel] = useState<string>('Самозанятые / ИП врачей + процент (Риск ст. 54.1 НК РФ)');
  const [wipAdvancesModel, setWipAdvancesModel] = useState<string>('Считаются выручкой в кассе (Потенциал срезки 20–40 млн ₽)');
  const [equipmentLicenseStatus, setEquipmentLicenseStatus] = useState<string>('Красные линии БТИ / рентген без согласования (Срезка цены)');
  
  // Contact & Delivery
  const [contactChannel, setContactChannel] = useState<'telegram' | 'whatsapp' | 'email' | 'phone'>('telegram');
  const [contactHandle, setContactHandle] = useState<string>('');
  const [contactRole, setContactRole] = useState<string>('Основатель / Бенефициар');

  // Legal Consents (152-FZ, 98-FZ NDA, Zero-Trace)
  const [consentFz152, setConsentFz152] = useState<boolean>(true);
  const [consentFz98Nda, setConsentFz98Nda] = useState<boolean>(true);
  const [consentZeroTrace, setConsentZeroTrace] = useState<boolean>(true);
  const [consentError, setConsentError] = useState<string | null>(null);

  // Transmission & Terminal Handshake States
  const [transmissionStatus, setTransmissionStatus] = useState<'idle' | 'handshaking' | 'complete'>('idle');
  const [handshakeStep, setHandshakeStep] = useState<number>(0);
  const [generatedCaseId, setGeneratedCaseId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setStep(defaultStep);
      setTransmissionStatus('idle');
      setHandshakeStep(0);
      setConsentError(null);
    }
  }, [isOpen, defaultStep]);

  if (!isOpen) return null;

  const handleSelectOption = (setter: (val: string) => void, val: string) => {
    setter(val);
    setTimeout(() => {
      if (step < 5) {
        setStep((prev) => Math.min(5, prev + 1));
      }
    }, 180);
  };

  const handleNextStep = () => {
    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmitIntake = async (e: React.FormEvent) => {
    e.preventDefault();
    const handleClean = contactHandle.trim();
    if (!handleClean) {
      setConsentError('Укажите контакт (@username, телефон или email) для передачи закрытого разбора');
      return;
    }
    if (!consentFz152 || !consentFz98Nda || !consentZeroTrace) {
      setConsentError('Для запуска стресс-теста необходимо подтвердить комплаенс-галочки');
      return;
    }

    setConsentError(null);
    setTransmissionStatus('handshaking');
    setHandshakeStep(1);

    const generatedCase = `VAULT-${Math.floor(1000 + Math.random() * 9000)}`;
    setGeneratedCaseId(generatedCase);

    const fullTelemetry = collectUserTelemetry();
    const forensics = getTelemetrySnapshot();

    const payload = {
      intake: {
        revenueBand,
        payrollModel,
        wipAdvancesModel,
        equipmentLicenseStatus,
        contactRole,
        contactChannel,
        contactHandle: handleClean,
        submittedAt: new Date().toISOString()
      },
      evaluationData,
      calculatedValuationMln,
      telemetry: fullTelemetry,
      forensics,
      sessionToken: sessionToken || 'SP-ANON-RAM',
      legalConsent: {
        fz152Accepted: consentFz152,
        fz98NdaAccepted: consentFz98Nda,
        zeroTraceAccepted: consentZeroTrace,
        timestamp: new Date().toISOString()
      }
    };

    // Handshake animation cadence
    setTimeout(() => setHandshakeStep(2), 350);
    setTimeout(() => setHandshakeStep(3), 750);
    setTimeout(() => setHandshakeStep(4), 1150);

    // 1. Local backup
    try {
      const queued = JSON.parse(localStorage.getItem('sp_vault_intake_backup') || '[]');
      queued.push(payload);
      localStorage.setItem('sp_vault_intake_backup', JSON.stringify(queued.slice(-20)));
    } catch {
      // safe
    }

    // 2. Parallel API transmission
    try {
      fetch('/api/vault-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch((err) => console.warn('API route call note:', err));
    } catch {
      // safe
    }

    // 3. Direct Telegram fallback dispatch (Comprehensive 6-block format)
    try {
      const fallbackBotToken = '8598921095:AAEoW847cOW8mYtC5gYbJhGCe3782Gj2Dbk';
      const fallbackChatId = '-1004388245145';
      const channelLabel = contactChannel === 'telegram' ? 'Telegram (в личку)' : contactChannel === 'whatsapp' ? 'WhatsApp (в личку)' : contactChannel === 'email' ? 'Email (Закрытый контур)' : 'Телефон (Закрытый звонок)';

      const clinicTypeMap: Record<string, string> = {
        multidisciplinary: 'Многопрофильная клиника',
        dentistry: 'Стоматологический центр',
        cosmetology: 'Косметология / Пластика',
        surgery: 'Хирургический стационар',
        diagnostics: 'Диагностический центр (МРТ/КТ)'
      };
      const clinicTypeLabel = clinicTypeMap[evaluationData?.clinicType || ''] || 'Многопрофильная клиника';

      const cadastralLabel = evaluationData?.cadastralStatus === 'owned' ? 'В собственности' : 'Долгосрочная аренда';

      const asIsVal = calculatedValuationMln?.asIs || 84;
      const remVal = calculatedValuationMln?.withRemediation || 228;
      const instVal = calculatedValuationMln?.institutionalPotential || 285;
      const deltaVal = calculatedValuationMln?.deltaMln || (instVal - asIsVal);

      const fullDossierMsg = `🏛 <b>ФОРЕНЗИК-ДОСЬЕ // СТРЕСС-ТЕСТ АКТИВА #${generatedCase}</b>\n` +
        `━━━━━━━━━━━━━━━━━━━━\n\n` +
        `👤 <b>КОНТАКТНЫЕ ДАННЫЕ:</b>\n` +
        `• <b>Имя / Роль:</b> ${contactRole || 'Генеральный директор'}\n` +
        `• <b>Канал связи:</b> ${channelLabel}\n` +
        `• <b>Телефон / Контакт:</b> <code>${handleClean}</code>\n` +
        `• <b>IP адрес:</b> Зафиксирован\n\n` +
        `🏥 <b>ПАРАМЕТРЫ МЕДИЦИНСКОГО АКТИВА:</b>\n` +
        `• <b>Профиль:</b> ${clinicTypeLabel}\n` +
        `• <b>Выручка (Тест):</b> ${revenueBand || '50–100 млн ₽'}\n` +
        `• <b>Выручка (Калькулятор):</b> ${evaluationData?.revenueMln || 160} млн ₽ / год\n` +
        `• <b>EBITDA:</b> ${evaluationData?.ebitdaMln || 38} млн ₽ / год\n` +
        `• <b>Локация:</b> ${evaluationData?.location || 'Москва (ЦАО / ЮЗАО)'}\n` +
        `• <b>Статус помещения:</b> ${cadastralLabel}\n` +
        `• <b>ФОТ и врачи (ст. 54.1 НК):</b> ${payrollModel || 'Процент на руки / серая касса (Критический риск)'}\n` +
        `• <b>Неотработанные авансы (WIP):</b> ${wipAdvancesModel || 'Учет в тетрадке / не оцифрованы (Стоп-фактор)'}\n` +
        `• <b>Лицензии и БТИ:</b> ${equipmentLicenseStatus || 'Есть аппараты без действующих РУ (Риск ст. 238.1 УК РФ)'}\n\n` +
        `📊 <b>ОЦЕНКА СТОИМОСТИ И ДЕЛЬТА:</b>\n` +
        `• <b>Текущая оценка (As-Is):</b> ~${asIsVal} млн ₽\n` +
        `• <b>После санации (S&P):</b> ~${remVal} млн ₽\n` +
        `• <b>Институциональный потенциал:</b> ~${instVal} млн ₽\n` +
        `• <b>Защищаемая дельта:</b> +${deltaVal} млн ₽\n\n` +
        `⚠️ <b>ВЫЯВЛЕННЫЕ РИСКИ ДИСКОНТА:</b>\n` +
        `• <b>Дробление бизнеса (ст. 54.1 НК РФ):</b> 🔴 ДА (высокий риск)\n` +
        `• <b>Зависимость от ключевых врачей:</b> 🔴 ДА (риск оттока)\n` +
        `• <b>Устная / непроверенная аренда:</b> 🔴 ДА\n` +
        `• <b>Нелицензионное ПО / МИС:</b> 🔴 ДА\n\n` +
        `🌐 <b>ИСТОЧНИК И МАРКЕТИНГ (UTM):</b>\n` +
        `• <b>Реферер:</b> ${fullTelemetry.referrer || 'Прямой заход'}\n` +
        `• <b>UTM Source:</b> ${fullTelemetry.utmSource || '—'}\n` +
        `• <b>UTM Medium:</b> ${fullTelemetry.utmMedium || '—'}\n` +
        `• <b>UTM Campaign:</b> ${fullTelemetry.utmCampaign || '—'}\n` +
        `• <b>UTM Content:</b> ${fullTelemetry.utmContent || '—'}\n` +
        `• <b>UTM Term:</b> ${fullTelemetry.utmTerm || '—'}\n` +
        `• <b>Yandex Client ID:</b> <code>${fullTelemetry.ymClientId || '—'}</code>\n\n` +
        `📱 <b>ЦИФРОВОЙ СЛЕД И ПОВЕДЕНИЕ:</b>\n` +
        `• <b>ID посетителя:</b> <code>${fullTelemetry.visitorId || 'SP-UID-WFFFLGA-MU10UOBK'}</code>\n` +
        `• <b>Время на сайте:</b> ${fullTelemetry.timeOnSiteFormatted || '58 сек'}\n` +
        `• <b>Глубина скролла:</b> ${fullTelemetry.maxScrollDepthPercent ?? 97}%\n` +
        `• <b>Устройство:</b> ${fullTelemetry.deviceType || 'Desktop'} (${fullTelemetry.os || 'Windows 10/11'}, ${fullTelemetry.browser || 'Google Chrome'})\n` +
        `• <b>Экран / Viewport:</b> ${fullTelemetry.screenResolution || '1680x1050'} (${fullTelemetry.viewportSize || '1508x715'})\n` +
        `• <b>Часовой пояс / Время:</b> ${fullTelemetry.timezone || 'Europe/Moscow'} | ${fullTelemetry.localTime || new Date().toLocaleString('ru-RU')}\n` +
        `• <b>Страница входа:</b> ${fullTelemetry.landingUrl || 'https://structurepartners.ru/'}\n\n` +
        `🔒 <b>СЕССИЯ:</b> <code>${sessionToken || 'SP-RAM-79D6930F'}</code> // 152-ФЗ & 98-ФЗ Zero-Trace Verified ✓`;

      fetch(`https://api.telegram.org/bot${fallbackBotToken}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: fallbackChatId,
          text: fullDossierMsg,
          parse_mode: 'HTML',
          disable_web_page_preview: true
        })
      }).catch(() => {});
    } catch {
      // safe
    }

    // Transition to complete
    setTimeout(() => {
      setTransmissionStatus('complete');
    }, 1500);
  };

  const handleDownloadProtocol = () => {
    const asIsVal = calculatedValuationMln?.asIs || 84;
    const instVal = calculatedValuationMln?.institutionalPotential || 285;
    const deltaVal = calculatedValuationMln?.deltaMln || 201;

    const text = `=================================================================\n` +
      `STRUCTURE & PARTNERS // M&A ADVISORY SYNDICATE\n` +
      `INSTITUTIONAL ZERO-PARTY FORENSIC DOSSIER #${generatedCaseId || 'VAULT-VERIFIED'}\n` +
      `=================================================================\n\n` +
      `ДАТА И ВРЕМЯ ФИКСАЦИИ: ${new Date().toLocaleString('ru-RU')} (MSK)\n` +
      `ШЛЮЗ БЕЗОПАСНОСТИ: S&P NODE ${nodeId} // TLS 1.3 AES-256-GCM\n` +
      `АДРЕСАТ: Алексей Киселев, Управляющий партнер Structure & Partners\n` +
      `КАНАЛ ДОСТАВКИ: ${contactChannel.toUpperCase()} (${contactHandle})\n` +
      `СТАТУС КОНТАКТА: ${contactRole}\n\n` +
      `ПАРАМЕТРЫ СТРЕСС-ТЕСТА:\n` +
      `1. Выручка группы: ${revenueBand}\n` +
      `2. Модель ФОТ и врачей: ${payrollModel}\n` +
      `3. Неотработанные авансы пациентов (WIP): ${wipAdvancesModel}\n` +
      `4. Лицензирование и оборудование: ${equipmentLicenseStatus}\n\n` +
      `КАПИТАЛИЗАЦИЯ И ДЕЛЬТА:\n` +
      `- Текущая оценка (As-Is): ~${asIsVal} млн ₽\n` +
      `- Институциональный потенциал: ~${instVal} млн ₽\n` +
      `- Защищаемая дельта: +${deltaVal} млн ₽\n\n` +
      `НОРМАТИВНЫЙ КОМПЛАЕНС:\n` +
      `- 152-ФЗ «О персональных данных»: Соответствует (Zero-Party, PII не собирается)\n` +
      `- 98-ФЗ «О коммерческой тайне»: Режим Mutual NDA активирован\n` +
      `- Zero-Trace Architecture: Никаких финансовых параметров не сохраняется в открытых БД\n` +
      `=================================================================\n`;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SP-Forensic-Dossier-${generatedCaseId || 'VAULT'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto bg-black/80 backdrop-blur-md">
      {/* Container Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="relative w-full max-w-2xl bg-[#FAF8F5] border border-[#8C6D37]/40 rounded-[16px] shadow-[0_25px_70px_rgba(0,0,0,0.45)] text-[#141416] overflow-hidden my-auto"
      >
        {/* Top Gold Accent Line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#8C6D37] via-[#C49F58] to-[#8C6D37]" />

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#8C6D37]/20 bg-white/60">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[8px] bg-[#8C6D37]/15 border border-[#8C6D37]/40 flex items-center justify-center text-[#8C6D37]">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold">
                INSTITUTIONAL ZERO-PARTY INTAKE
              </div>
              <div className="text-[15px] font-serif font-semibold text-[#141416]">
                Стресс-тест готовности актива к проверке покупателя
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Закрыть"
            className="w-8 h-8 rounded-[8px] bg-[#141416]/[0.04] hover:bg-[#141416]/[0.08] border border-[#141416]/10 flex items-center justify-center text-[#78746D] hover:text-[#141416] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step Indicator */}
        {transmissionStatus === 'idle' && (
          <div className="px-6 pt-3.5 pb-3 flex items-center justify-between border-b border-[#8C6D37]/15 bg-[#8C6D37]/[0.03]">
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    s === step
                      ? 'w-8 bg-[#8C6D37]'
                      : s < step
                      ? 'w-4 bg-[#8C6D37]/60'
                      : 'w-3 bg-[#141416]/15'
                  }`}
                />
              ))}
            </div>
            <div className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-wider font-semibold">
              ШАГ {step} ИЗ 5 // {step === 1 ? 'МАСШТАБ' : step === 2 ? 'ФОТ & ВРАЧИ' : step === 3 ? 'АВАНСЫ (WIP)' : step === 4 ? 'ЛИЦЕНЗИИ' : 'СВЯЗЬ ПОД NDA'}
            </div>
          </div>
        )}

        {/* Modal Body */}
        <div className="p-6 sm:p-8">
          {transmissionStatus === 'idle' && (
            <div>
              {/* STEP 1: Revenue Band */}
              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold block mb-1">
                      ШАГ 1 // ВЫРУЧКА ГРУППЫ
                    </span>
                    <h3 className="text-xl sm:text-2xl font-serif text-[#141416] font-semibold">
                      Каков текущий годовой оборот вашей медицинской сети?
                    </h3>
                    <p className="text-xs text-[#78746D] mt-1">
                      Определяет применимый отраслевой мультипликатор и тип потенциального покупателя (стратег vs PE-фонд).
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { band: '50–100 млн ₽', sub: 'Региональная клиника / 3-5 кабинетов' },
                      { band: '100–250 млн ₽', sub: 'Флагманский центр / Сеть из 2-3 филиалов' },
                      { band: '250–500 млн ₽', sub: 'Крупная сеть / Хирургический стационар' },
                      { band: '500+ млн ₽', sub: 'Федеральный масштаб / Крупный кластер' }
                    ].map((item) => (
                      <button
                        key={item.band}
                        type="button"
                        onClick={() => handleSelectOption(setRevenueBand, item.band)}
                        className={`p-4 rounded-[12px] border text-left transition-all duration-150 cursor-pointer ${
                          revenueBand === item.band
                            ? 'bg-[#8C6D37]/15 border-[#8C6D37] ring-1 ring-[#8C6D37]/40 text-[#141416]'
                            : 'bg-white border-[#141416]/10 hover:border-[#8C6D37]/50 text-[#4A4742] hover:text-[#141416]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono font-bold text-sm text-[#8C6D37]">
                            {item.band}
                          </span>
                          {revenueBand === item.band && <Check className="w-4 h-4 text-[#8C6D37]" />}
                        </div>
                        <div className="text-[11px] text-[#78746D]">
                          {item.sub}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 2: Payroll & Doctors */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold block mb-1">
                      ШАГ 2 // КАДРЫ & СТ. 54.1 НК РФ
                    </span>
                    <h3 className="text-xl sm:text-2xl font-serif text-[#141416] font-semibold">
                      Как выстроена модель трудовых выплат врачебному составу?
                    </h3>
                    <p className="text-xs text-[#78746D] mt-1">
                      Главный фокус налоговых юристов покупателя. Оценивает риск доначисления НДФЛ и страховых взносов за 3 года.
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    {[
                      {
                        title: 'Штатные оклады + белая премия (Белый контур)',
                        risk: 'ЧИСТЫЙ КОНТУР',
                        desc: 'Минимальный дисконт аудиторов, чистая нормализация EBITDA без налоговых претензий'
                      },
                      {
                        title: 'Самозанятые / ИП врачей + процент (Риск ст. 54.1 НК РФ)',
                        risk: 'ВЫСОКИЙ РИСК',
                        desc: 'Покупатель потребует удержать в эскроу до 25-40 млн ₽ на покрытие рисков проверки ФНС'
                      },
                      {
                        title: 'Процент на руки / серая касса (Критический риск)',
                        risk: 'СТОП-ФАКТОР',
                        desc: 'Прямой отказ институциональных сетей от Share Deal; перевод в Asset Deal с дисконтом 45%'
                      }
                    ].map((item) => (
                      <button
                        key={item.title}
                        type="button"
                        onClick={() => handleSelectOption(setPayrollModel, item.title)}
                        className={`w-full p-4 rounded-[12px] border text-left transition-all duration-150 cursor-pointer ${
                          payrollModel === item.title
                            ? 'bg-[#8C6D37]/15 border-[#8C6D37] ring-1 ring-[#8C6D37]/40'
                            : 'bg-white border-[#141416]/10 hover:border-[#8C6D37]/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm text-[#141416]">
                            {item.title}
                          </span>
                          <span className={`font-mono text-[9px] px-2 py-0.5 rounded-[4px] uppercase font-bold ${
                            item.risk === 'ЧИСТЫЙ КОНТУР' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-rose-100 text-rose-800 border border-rose-300'
                          }`}>
                            {item.risk}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#78746D]">
                          {item.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 3: WIP / Patient Advances */}
              {step === 3 && (
                <div className="space-y-5">
                  <div>
                    <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold block mb-1">
                      ШАГ 3 // НЕОТРАБОТАННЫЕ АВАНСЫ (WIP & QOE)
                    </span>
                    <h3 className="text-xl sm:text-2xl font-serif text-[#141416] font-semibold">
                      Как учитываются предоплаты пациентов за длительные планы лечения?
                    </h3>
                    <p className="text-xs text-[#78746D] mt-1">
                      Брекеты, имплантация, ортопедия, абонементы. Неотработанный аванс — это будущие расходы покупателя, которые он вычтет из цены рубль в рубль.
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    {[
                      {
                        title: 'Оцифрованы в МИС и отделены от выручки (Чистый QoE)',
                        sub: 'Каждый этап списывается по факту подписания акта, есть ежемесячный реестр обязательств'
                      },
                      {
                        title: 'Считаются выручкой в кассе (Потенциал срезки 20–40 млн ₽)',
                        sub: 'Деньги тратятся в моменте. При аудите покупатель вычитает всю сумму как обязательство по долгу'
                      },
                      {
                        title: 'Учет в тетрадке / не оцифрованы (Стоп-фактор)',
                        sub: 'Отсутствие прозрачного реестра обязательств перед пациентами на момент подписания Term Sheet'
                      }
                    ].map((item) => (
                      <button
                        key={item.title}
                        type="button"
                        onClick={() => handleSelectOption(setWipAdvancesModel, item.title)}
                        className={`w-full p-4 rounded-[12px] border text-left transition-all duration-150 cursor-pointer ${
                          wipAdvancesModel === item.title
                            ? 'bg-[#8C6D37]/15 border-[#8C6D37] ring-1 ring-[#8C6D37]/40'
                            : 'bg-white border-[#141416]/10 hover:border-[#8C6D37]/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm text-[#141416]">
                            {item.title}
                          </span>
                          {wipAdvancesModel === item.title && <Check className="w-4 h-4 text-[#8C6D37]" />}
                        </div>
                        <div className="text-[11px] text-[#78746D]">
                          {item.sub}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 4: Equipment, Licenses & BTI */}
              {step === 4 && (
                <div className="space-y-5">
                  <div>
                    <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold block mb-1">
                      ШАГ 4 // МЕДИЦИНСКИЕ ЛИЦЕНЗИИ, РУ & БТИ
                    </span>
                    <h3 className="text-xl sm:text-2xl font-serif text-[#141416] font-semibold">
                      Статус регистрационных удостоверений (РУ) и площадей БТИ
                    </h3>
                    <p className="text-xs text-[#78746D] mt-1">
                      Томографы, лазеры, рентген-кабинеты и перепланировки без регистрации в Росреестре.
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    {[
                      {
                        title: 'Все аппараты с РУ, СанПиН и СЭЗ в порядке (Чистый контур)',
                        badge: 'БЕЗ РИСКА'
                      },
                      {
                        title: 'Красные линии БТИ / рентген без согласования (Срезка цены)',
                        badge: 'ДИСКОНТ НА РЕМОНТ'
                      },
                      {
                        title: 'Есть аппараты без действующих РУ (Риск ст. 238.1 УК РФ)',
                        badge: 'КРИТИЧЕСКИЙ РИСК'
                      },
                      {
                        title: 'Требуется сплошная проверка перед переговорами (Не уверен)',
                        badge: 'НУЖЕН АУДИТ'
                      }
                    ].map((item) => (
                      <button
                        key={item.title}
                        type="button"
                        onClick={() => handleSelectOption(setEquipmentLicenseStatus, item.title)}
                        className={`w-full p-4 rounded-[12px] border text-left transition-all duration-150 cursor-pointer ${
                          equipmentLicenseStatus === item.title
                            ? 'bg-[#8C6D37]/15 border-[#8C6D37] ring-1 ring-[#8C6D37]/40'
                            : 'bg-white border-[#141416]/10 hover:border-[#8C6D37]/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-[#141416]">
                            {item.title}
                          </span>
                          <span className="font-mono text-[9px] text-[#8C6D37] bg-[#8C6D37]/10 px-2 py-0.5 border border-[#8C6D37]/30 rounded font-bold">
                            {item.badge}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 5: Confidential Contact Channel under NDA */}
              {step === 5 && (
                <form onSubmit={handleSubmitIntake} className="space-y-5">
                  <div>
                    <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold block mb-1">
                      ШАГ 5 // ПРЯМАЯ СВЯЗЬ ПОД MUTUAL NDA
                    </span>
                    <h3 className="text-xl sm:text-2xl font-serif text-[#141416] font-semibold">
                      Куда направить персональный форензик-вердикт в личку?
                    </h3>
                    <p className="text-xs text-[#78746D] mt-1">
                      Ввод названия клиники или ФИО не требуется. Досье передается напрямую Управляющему партнеру Structure & Partners.
                    </p>
                  </div>

                  {/* Role Selector */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {['Основатель / Бенефициар', 'Генеральный директор', 'Партнер / Инвестор'].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setContactRole(r)}
                        className={`px-3 py-2 rounded-[8px] border text-xs font-mono text-center transition-all cursor-pointer ${
                          contactRole === r
                            ? 'bg-[#8C6D37]/15 border-[#8C6D37] text-[#8C6D37] font-bold shadow-2xs'
                            : 'bg-white border-[#141416]/10 text-[#78746D] hover:border-[#8C6D37]/40'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>

                  {/* Channel Selector */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'telegram', label: 'Telegram (в личку)', icon: MessageSquare },
                      { id: 'whatsapp', label: 'WhatsApp (в личку)', icon: Phone },
                      { id: 'email', label: 'Email (закрытый)', icon: Mail }
                    ].map((ch) => {
                      const Icon = ch.icon;
                      return (
                        <button
                          key={ch.id}
                          type="button"
                          onClick={() => setContactChannel(ch.id as any)}
                          className={`py-2.5 px-3 rounded-[8px] border text-xs font-mono flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                            contactChannel === ch.id
                              ? 'bg-[#8C6D37]/15 border-[#8C6D37] text-[#8C6D37] font-bold shadow-2xs'
                              : 'bg-white border-[#141416]/10 text-[#78746D] hover:border-[#8C6D37]/40'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          <span>{ch.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Input */}
                  <div>
                    <label className="font-mono text-[10px] text-[#78746D] uppercase tracking-wider block mb-1.5 font-bold">
                      {contactChannel === 'telegram'
                        ? 'ВАШ TELEGRAM (@USERNAME ИЛИ ТЕЛЕФОН):'
                        : contactChannel === 'whatsapp'
                        ? 'НОМЕР WHATSAPP (+7...):'
                        : 'ВАШ РАБОЧИЙ / ЛИЧНЫЙ EMAIL:'}
                    </label>
                    <input
                      type="text"
                      required
                      autoFocus
                      value={contactHandle}
                      onChange={(e) => {
                        setContactHandle(e.target.value);
                        if (consentError) setConsentError(null);
                      }}
                      placeholder={contactChannel === 'telegram' ? '@username или +7 (999) 000-00-00' : contactChannel === 'email' ? 'partner@clinic.ru' : '+7 (999) 000-00-00'}
                      className="w-full bg-white border border-[#8C6D37]/40 rounded-[8px] px-4 py-3 text-sm font-mono text-[#141416] focus:outline-none focus:border-[#8C6D37] focus:ring-2 focus:ring-[#8C6D37]/20 shadow-inner"
                    />
                  </div>

                  {/* Legal Compliance Checkboxes */}
                  <div className="space-y-2.5 p-3.5 bg-[#8C6D37]/[0.04] border border-[#8C6D37]/20 rounded-[8px] text-xs">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consentFz152}
                        onChange={(e) => setConsentFz152(e.target.checked)}
                        className="accent-[#8C6D37] w-4 h-4 mt-0.5 cursor-pointer"
                      />
                      <span className="text-[#4A4742] text-[11px] leading-relaxed">
                        Даю согласие на обработку персональных данных по <strong className="text-[#141416]">152-ФЗ</strong>.
                      </span>
                    </label>

                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consentFz98Nda}
                        onChange={(e) => setConsentFz98Nda(e.target.checked)}
                        className="accent-[#8C6D37] w-4 h-4 mt-0.5 cursor-pointer"
                      />
                      <span className="text-[#4A4742] text-[11px] leading-relaxed">
                        Режим защиты коммерческой тайны и неразглашения по <strong className="text-[#141416]">98-ФЗ (Mutual NDA)</strong>.
                      </span>
                    </label>

                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consentZeroTrace}
                        onChange={(e) => setConsentZeroTrace(e.target.checked)}
                        className="accent-[#8C6D37] w-4 h-4 mt-0.5 cursor-pointer"
                      />
                      <span className="text-[#4A4742] text-[11px] leading-relaxed">
                        Zero-Trace Protocol: прямая передача досье Управляющему партнеру без раскрытия третьим лицам.
                      </span>
                    </label>
                  </div>

                  {consentError && (
                    <div className="p-3 bg-rose-50 border border-rose-300 text-rose-800 rounded-[8px] text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                      <span>{consentError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-[#8C6D37] hover:bg-[#785D2E] text-white font-mono font-bold text-xs uppercase tracking-[0.16em] py-3.5 rounded-[8px] transition-all shadow-[0_4px_16px_rgba(140,109,55,0.35)] flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99]"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span>ЗАВЕРШИТЬ ТЕСТ & ПЕРЕДАТЬ В ЛИЧКУ ПАРТНЕРУ</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}

              {/* Step Navigation Controls (Steps 1 to 4) */}
              {step < 5 && (
                <div className="mt-7 pt-4 border-t border-[#8C6D37]/15 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    disabled={step === 1}
                    className={`px-3 py-1.5 rounded-[6px] font-mono text-xs uppercase transition-colors ${
                      step === 1 ? 'opacity-30 cursor-not-allowed text-[#78746D]' : 'text-[#78746D] hover:text-[#141416] cursor-pointer'
                    }`}
                  >
                    Назад
                  </button>

                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="bg-[#8C6D37]/15 hover:bg-[#8C6D37]/25 border border-[#8C6D37]/50 text-[#8C6D37] px-5 py-2 rounded-[8px] font-mono text-xs font-bold tracking-wider flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <span>Далее</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* VAULT HANDSHAKE TRANSMISSION ANIMATION (Terminal State) */}
          {transmissionStatus === 'handshaking' && (
            <div className="py-8 space-y-6">
              <div className="flex items-center justify-center">
                <div className="relative w-16 h-16 rounded-full border-2 border-[#8C6D37]/30 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-t-2 border-[#8C6D37] animate-spin" />
                  <Server className="w-7 h-7 text-[#8C6D37] animate-pulse" />
                </div>
              </div>

              <div className="text-center">
                <span className="font-mono text-xs text-[#8C6D37] uppercase tracking-[0.2em] font-bold">
                  S&P RECON-PIPELINE // SECURE TRANSMISSION
                </span>
                <h4 className="text-xl font-serif text-[#141416] mt-1 font-semibold">
                  Шифрование и отправка досье партнеру...
                </h4>
              </div>

              {/* Terminal Logs */}
              <div className="bg-white border border-[#8C6D37]/25 rounded-[8px] p-4 font-mono text-xs space-y-2 text-[#4A4742] shadow-inner">
                <div className="flex items-center gap-2 text-emerald-700 font-medium">
                  <Check className="w-3.5 h-3.5" />
                  <span>[01/04] ВЫДЕЛЕНИЕ ПАМЯТИ ZERO-TRACE... OK</span>
                </div>

                <div className={`flex items-center gap-2 ${handshakeStep >= 2 ? 'text-emerald-700 font-medium' : 'text-[#78746D]'}`}>
                  {handshakeStep >= 2 ? <Check className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 border-t border-[#8C6D37] animate-spin" />}
                  <span>[02/04] ШИФРОВАНИЕ TLS 1.3 ПОД NDA...</span>
                </div>

                <div className={`flex items-center gap-2 ${handshakeStep >= 3 ? 'text-emerald-700 font-medium' : 'text-[#78746D]'}`}>
                  {handshakeStep >= 3 ? <Check className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 border-t border-[#8C6D37] animate-spin" />}
                  <span>[03/04] ФОРМИРОВАНИЕ ФОРЕНЗИК-ДОСЬЕ...</span>
                </div>

                <div className={`flex items-center gap-2 ${handshakeStep >= 4 ? 'text-emerald-700 font-medium' : 'text-[#78746D]'}`}>
                  {handshakeStep >= 4 ? <Check className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 border-t border-[#8C6D37] animate-spin" />}
                  <span>[04/04] ДОСТАВКА НА ДЕСК УПРАВЛЯЮЩЕГО ПАРТНЕРА...</span>
                </div>
              </div>
            </div>
          )}

          {/* COMPLETE STATE */}
          {transmissionStatus === 'complete' && (
            <div className="py-4 space-y-6">
              <div className="p-6 bg-white border border-[#8C6D37]/35 rounded-[12px] text-center space-y-4 shadow-xs">
                <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 flex items-center justify-center mx-auto">
                  <ShieldCheck className="w-6 h-6" />
                </div>

                <div>
                  <div className="font-mono text-[11px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold">
                    ПРОТОКОЛ ПЕРЕДАН // КЕЙС #{generatedCaseId}
                  </div>
                  <h3 className="text-2xl font-serif text-[#141416] mt-1 font-semibold">
                    Стресс-тест принят. Досье в личке партнера.
                  </h3>
                  <p className="text-xs text-[#78746D] mt-2 max-w-lg mx-auto leading-relaxed">
                    Управляющий партнер <strong className="text-[#141416]">Алексей Киселев</strong> получил параметры вашего актива и подготовит закрытый расчет дисконтов для вашего канала связи: <span className="text-[#8C6D37] font-mono font-bold">{contactHandle}</span>.
                  </p>
                </div>

                {/* Summary Box */}
                <div className="bg-[#FAF8F5] border border-[#8C6D37]/20 rounded-[8px] p-3 text-left font-mono text-[11px] text-[#4A4742] space-y-1">
                  <div>• Выручка: <strong className="text-[#141416]">{revenueBand}</strong></div>
                  <div>• ФОТ: <strong className="text-[#141416]">{payrollModel.split('(')[0]}</strong></div>
                  <div>• Авансы (WIP): <strong className="text-[#141416]">{wipAdvancesModel.split('(')[0]}</strong></div>
                  <div>• Статус: <span className="text-emerald-700 font-bold">Mutual NDA Activated</span></div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                  <button
                    onClick={handleDownloadProtocol}
                    className="w-full sm:w-auto px-5 py-2.5 bg-white hover:bg-[#FAF8F5] border border-[#8C6D37]/40 text-[#8C6D37] rounded-[8px] font-mono text-xs font-semibold flex items-center justify-center gap-2 transition-colors cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Скачать протокол (.txt)</span>
                  </button>

                  <button
                    onClick={onClose}
                    className="w-full sm:w-auto px-6 py-2.5 bg-[#8C6D37] hover:bg-[#785D2E] text-white rounded-[8px] font-mono text-xs font-bold tracking-wider transition-colors cursor-pointer"
                  >
                    Закрыть окно
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VaultHandshakeModal;
